import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, TextField, InputAdornment, IconButton, List, ListItem, ListItemText, Paper, Avatar, Chip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { supabase } from '../lib/supabase';

// Profile interface matching profiles table schema
interface ProfileSuggestion {
  id: string;
  username: string;
  avatar_url?: string;
}

interface MentionInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
  fullWidth?: boolean;
  variant?: 'outlined' | 'filled' | 'standard';
  disabled?: boolean;
  label?: string;
  onMention?: (mentionedUsers: string[]) => void;
}

const MentionInput: React.FC<MentionInputProps> = ({
  value,
  onChange,
  placeholder = 'Write a comment...',
  multiline = false,
  rows = 1,
  fullWidth = true,
  variant = 'outlined',
  disabled = false,
  label,
  onMention
}) => {
  const [suggestions, setSuggestions] = useState<ProfileSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const [mentionStart, setMentionStart] = useState(-1);
  const [mentionedUsers, setMentionedUsers] = useState<ProfileSuggestion[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Function to extract @mentions
  const extractMentions = useCallback((text: string): string[] => {
    const mentionRegex = /@(\w+)/g;
    const mentions = [];
    let match;
    
    while ((match = mentionRegex.exec(text)) !== null) {
      mentions.push(match[1]);
    }
    
    return Array.from(new Set(mentions)); // Remove duplicates using Array.from()
  }, []);

  // Function to fetch profile suggestions
  const fetchProfiles = useCallback(async (searchQuery: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, username, avatar_url')
        .ilike('username', `%${searchQuery}%`)
        .limit(5);
      
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching profile suggestions:', error);
      return [];
    }
  }, []);

  // Function to fetch profile data for mentioned users
  const fetchMentionedProfiles = useCallback(async (mentions: string[]) => {
    if (!mentions.length) return [];
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, username, avatar_url')
        .in('username', mentions);
      
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching mentioned profiles:', error);
      return [];
    }
  }, []);

  // Update mentioned users when value changes
  useEffect(() => {
    const fetchMentions = async () => {
      const mentions = extractMentions(value);
      if (mentions.length > 0) {
        const profileResults = await fetchMentionedProfiles(mentions);
        setMentionedUsers(profileResults);
      } else {
        setMentionedUsers([]);
      }
    };
    
    fetchMentions();
  }, [value, extractMentions, fetchMentionedProfiles]);

  // Handle input change
  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const cursorPos = e.target.selectionStart || 0;
    setCursorPosition(cursorPos);
    
    // Find if we're in a mention context
    let lastAtSymbolPos = -1;
    for (let i = cursorPos - 1; i >= 0; i--) {
      if (inputValue[i] === '@') {
        lastAtSymbolPos = i;
        break;
      } else if (inputValue[i] === ' ' || inputValue[i] === '\n') {
        break;
      }
    }
    
    if (lastAtSymbolPos !== -1) {
      const query = inputValue.substring(lastAtSymbolPos + 1, cursorPos).trim();
      if (query.length > 0) {
        setSearchTerm(query);
        setMentionStart(lastAtSymbolPos);
        
        // Fetch profile suggestions
        const profileResults = await fetchProfiles(query);
        setSuggestions(profileResults);
        setShowSuggestions(profileResults.length > 0);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
    
    onChange(inputValue);
    
    // Notify about mentions if callback provided
    if (onMention) {
      const mentions = extractMentions(inputValue);
      onMention(mentions);
    }
  };

  // Handle selecting a suggestion
  const handleSuggestionClick = (profile: ProfileSuggestion) => {
    if (mentionStart >= 0) {
      const before = value.substring(0, mentionStart);
      const after = value.substring(cursorPosition);
      const newValue = `${before}@${profile.username} ${after}`;
      
      onChange(newValue);
      
      // Reset suggestion state
      setShowSuggestions(false);
      setSuggestions([]);
      
      // Focus back on input and place cursor after the inserted mention
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          const newCursorPos = mentionStart + profile.username.length + 2; // +2 for @ and space
          inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 0);
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Box position="relative" ref={containerRef}>
      {/* Main text input */}
      <TextField
        inputRef={inputRef}
        value={value}
        onChange={handleInputChange}
        placeholder={placeholder}
        multiline={multiline}
        rows={rows}
        fullWidth={fullWidth}
        variant={variant}
        disabled={disabled}
        label={label}
        InputProps={{
          endAdornment: value && (
            <InputAdornment position="end">
              <IconButton
                edge="end"
                onClick={() => onChange('')}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          )
        }}
      />
      
      {/* Display suggestions */}
      {showSuggestions && (
        <Paper 
          ref={suggestionsRef}
          elevation={3} 
          sx={{ 
            position: 'absolute', 
            width: '100%', 
            maxHeight: 200, 
            overflow: 'auto',
            mt: 0.5,
            zIndex: 1300
          }}
        >
          <List dense>
            {suggestions.map((profile) => (
              <ListItem 
                key={profile.id} 
                button 
                onClick={() => handleSuggestionClick(profile)}
                sx={{
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <Avatar 
                  src={profile.avatar_url || ''} 
                  alt={profile.username} 
                  sx={{ width: 24, height: 24, mr: 1 }}
                />
                <ListItemText 
                  primary={profile.username}
                  primaryTypographyProps={{
                    sx: { fontWeight: 'medium' }
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
      
      {/* Mentioned users display (below input) */}
      {mentionedUsers.length > 0 && (
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', mr: 1 }}>
            Mentioned:
          </Typography>
          {mentionedUsers.map(user => (
            <Chip
              key={user.id}
              label={`@${user.username}`}
              size="small"
              color="primary"
              variant="outlined"
              avatar={user.avatar_url ? <Avatar src={user.avatar_url} /> : undefined}
              sx={{ 
                height: 24, 
                m: '0 4px 4px 0',
                fontWeight: 'bold',
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default MentionInput; 